import { ApiError } from '@kovix/api-client';
import { Button, Notice } from '@kovix/ui';
import { type ReactNode, useEffect, useState } from 'react';
import { api } from '@/shared/lib/api';
import { accountConsoleUrl, displayContext, ensureSignedIn, loginRealm, signOut } from './auth';
import { useSession } from './session.store';

/**
 * Khởi động phiên: đăng nhập OIDC → lấy quyền hiệu lực từ iam-svc → sẵn sàng.
 * Quyền lấy từ iam-svc chứ không đọc claim, để menu/route guard/nút cùng nguồn với kiểm quyền API (BRD §9.10).
 */
export function SessionBootstrap({ children }: { children: ReactNode }) {
  const status = useSession((s) => s.status);
  const setLoading = useSession((s) => s.setLoading);
  const setReady = useSession((s) => s.setReady);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading();
      try {
        await ensureSignedIn();
        const ctx = await displayContext();
        // Cổng /platform: token control plane chỉ vào được /api/v1/platform/** (gateway → 403 tenant.context_required ở /api/v1/iam/**)
        const perms = loginRealm === 'platform' ? await api.platformIam.myPermissions() : await api.iam.myPermissions();
        if (cancelled) return;
        setReady({ name: ctx.name, email: ctx.email }, ctx.tenantId, perms.data, ctx.tenantId ? 'tenant' : 'platform');
      } catch (e) {
        if (cancelled) return;
        const msg =
          e instanceof ApiError
            ? `${e.code} · ${e.message}${e.correlationId ? ` (${e.correlationId})` : ''}`
            : String(e);
        setErrorCode(e instanceof ApiError ? e.code : null);
        setError(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setLoading, setReady]);

  if (error) {
    // KOVIX-257 / BF59 (12/10/2026): gateway chặn iam.mfa_required cho vai trò quản trị nhưng Keycloak chưa ép cài OTP → trước đây màn chết, không lối thoát.
    // Dẫn sang Keycloak Account Console (mục Đăng nhập → Xác thực hai yếu tố) rồi đăng xuất để đăng nhập lại có OTP.
    const mfa = errorCode === 'iam.mfa_required';
    const otpUrl = `${accountConsoleUrl}/#/security/signingin`;
    return (
      <div style={{ padding: 40, maxWidth: 640 }}>
        <Notice tone={mfa ? 'warn' : 'bad'} icon={mfa ? '🔐' : '⛔'}>
          <b>{mfa ? 'Tài khoản của bạn bắt buộc xác thực hai yếu tố (OTP).' : 'Không khởi tạo được phiên làm việc.'}</b>
          <div style={{ marginTop: 6 }}>
            {mfa
              ? 'Vai trò quản trị phải đăng nhập bằng mật khẩu + OTP. Cài ứng dụng OTP trong Keycloak (mục Đăng nhập → Xác thực hai yếu tố), sau đó đăng xuất và đăng nhập lại.'
              : error}
          </div>
          {mfa ? <div className="kx-cellsub" style={{ marginTop: 4 }}>{error}</div> : null}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            {mfa ? (
              <Button variant="primary" onClick={() => window.open(otpUrl, '_blank', 'noopener')}>
                Cài OTP trong Keycloak
              </Button>
            ) : null}
            <Button onClick={() => void signOut()}>Đăng xuất</Button>
          </div>
        </Notice>
      </div>
    );
  }
  if (status !== 'ready') {
    return (
      <div style={{ padding: 40, color: 'var(--kx-muted)' }} aria-busy="true">
        Đang đăng nhập và tải quyền…
      </div>
    );
  }
  return <>{children}</>;
}
